import React, { useState } from "react";
import Button from "../Button/Button";
import { ReactComponent as PdfIcon } from "../../assets/icons/pdf.svg";
import { useAuth } from "../../contexts/AuthContext";
import { formatDate } from "../../utils/entityUtils";
import SuiviHeader from "../SuiviHeader/SuiviHeader";
import ReactDOMServer from "react-dom/server";

interface PrintButtonProps {
    targetRef: React.RefObject<HTMLElement>;
    fileName?: string;
    value?: string;
    className?: string;
    // Taille du bouton. Defaut `medium`, comme le Button : le bouton d'impression vit dans les memes
    // barres d'actions que les autres et doit avoir LA MEME HAUTEUR. (Il forcait `small` avant, ce qui
    // le rendait plus court d'un onglet a l'autre.) On garde le levier pour les usages inline.
    size?: 'small' | 'medium' | 'large';
    // Desactive l'impression (ex. tant que les plis ne sont pas dechiffres).
    disabled?: boolean;
    suiviHeaderProps: {
        titre: string;
        sousTitre: string;
        subtitle: string;
        status: string;
        statusColor: string;
        delais?: string;
        backgroundImage?: string;
    };
}

export const PrintButton: React.FC<PrintButtonProps> = ({
    targetRef,
    fileName = "document",
    value = "PDF",
    className = "",
    size = "medium",
    disabled = false,
    suiviHeaderProps
}) => {
    const { user } = useAuth();
    const [isLoading, setIsLoading] = useState(false);

    const syncFormValues = (source: HTMLElement, target: HTMLElement) => {
        const sync = (selector: string, prop: keyof HTMLInputElement) => {
            const src = source.querySelectorAll(selector);
            const tgt = target.querySelectorAll(selector);
            src.forEach((el, i) => ((tgt[i] as any)[prop] = (el as any)[prop]));
        };
        sync("select", "value");
        sync("textarea", "value");
        sync("input[type='text']", "value");
        sync("input[type='number']", "value");
        sync("input[type='checkbox']", "checked");
        sync("input[type='radio']", "checked");
    };

    const handlePrint = async () => {
        if (!targetRef.current || isLoading || disabled) return;
        setIsLoading(true);

        try {
            await new Promise<void>((resolve) => {
                const section = targetRef.current?.querySelector('.analyse-financiere-loaded');
                if (!section) {
                    // Si elle n'existe pas, on continue immédiatement
                    return resolve();
                }

                // Sinon, on attend qu’elle soit effectivement chargée
                const timeout = setTimeout(() => resolve(), 5000); // sécurité anti-blocage
                const check = () => {
                    if (targetRef.current?.querySelector('.analyse-financiere-loaded')) {
                        clearTimeout(timeout);
                        resolve();
                    } else {
                        setTimeout(check, 50);
                    }
                };
                check();
            });

            // 1. Attendre le chargement des images de la page affichée : sans leurs dimensions,
            //    le clone se mesure sur du vide.
            const images = Array.from(targetRef.current.querySelectorAll("img"));
            await Promise.all(
                images.map((img) => {
                    if (img.complete) return Promise.resolve();
                    return new Promise((resolve) => {
                        img.onload = img.onerror = () => resolve(null);
                    });
                })
            );

            await new Promise((resolve) => setTimeout(resolve, 300)); // petit délai pour le rendu

            // 2. Cloner le contenu et reporter les valeurs saisies (cloneNode ne copie que les
            //    attributs, pas l'état vivant des champs).
            const content = targetRef.current.cloneNode(true) as HTMLElement;
            syncFormValues(targetRef.current, content);


            // 3. REGLE DU SYSTEME : un document imprime ne porte QUE des valeurs.
            // Jamais une aide a la saisie. Un placeholder est une consigne adressee a
            // celui qui remplit le formulaire ; sur un PDF il n'a aucun destinataire, et
            // pire, il se lit comme une reponse. Un champ vide s'imprime vide.
            // Cette passe ne recopie aucun texte : elle en retire. Il n'y a donc rien a
            // maintenir en double — changer un placeholder dans un composant n'a aucun
            // effet sur l'impression.
            content.querySelectorAll("input, textarea").forEach((champ) => {
                champ.removeAttribute("placeholder");
            });

            // 4. Remplacer les textarea par un bloc statique
            // Un <textarea> n'imprime que sa portion visible : le texte au-dela de sa
            // hauteur est perdu. On le remplace par un bloc statique qui porte tout le
            // contenu. L'habillage vit dans print.css (.print-field), pas ici.
            const textareas = content.querySelectorAll("textarea");
            textareas.forEach((textarea) => {
                const div = document.createElement("div");
                div.className = "print-field print-field--multiline";
                div.innerText = textarea.value;
                textarea.parentNode?.replaceChild(div, textarea);
            });

            // 5. Remplacer les select par un bloc statique
            // Meme raison : un <select> imprime son widget, pas forcement lisiblement.
            //
            // PIEGE : dans ce projet le placeholder d'un select n'est pas un attribut,
            // c'est une vraie <option value=""> dont le texte est l'exemple de saisie.
            // Quand rien n'est choisi, c'est ELLE que `selectedIndex` designe. Recopier
            // son libelle faisait imprimer l'exemple comme s'il etait la reponse — le
            // document affirmait alors quelque chose de faux. On se fie donc a la VALEUR
            // du select, pas a l'index : valeur vide, cellule vide.
            const selects = content.querySelectorAll("select");
            selects.forEach((select) => {
                const selectedOption = select.options[select.selectedIndex];
                const div = document.createElement("div");
                div.className = "print-field";
                div.innerText = select.value ? selectedOption?.textContent || "" : "";
                select.parentNode?.replaceChild(div, select);
            });

            // 6. Collecter les feuilles de style de l'application
            const styles = Array.from(
                document.querySelectorAll("link[rel='stylesheet'], style")
            ).map((node) => node.outerHTML).join("\n");

            // 7. En-tête de suivi, rendu en HTML statique
            const suiviHeaderHTML = ReactDOMServer.renderToString(
                <SuiviHeader {...suiviHeaderProps} noNavigation={true} />
            );

            const now = new Date();
            const header = user?.email
                ? `<div style="font-size:1.2rem;font-weight:600;margin-bottom:20px;color:#333;">
                Imprimé par : ${user.email} le ${formatDate(now)}
              </div>`
                : "";

            // 8. Ouvrir la fenêtre et imprimer
            const printWindow = window.open("", "_blank");
            if (!printWindow) {
                // Popup bloquee par le navigateur. Sans ce retour, le clic ne produisait
                // RIEN et l'utilisateur ne pouvait pas savoir pourquoi.
                window.alert(
                    "L'impression a ete bloquee par le navigateur. Autorisez les fenetres " +
                    "surgissantes pour ce site, puis relancez l'impression."
                );
                return;
            }

            // Aucune feuille de style n'est ecrite ici : `styles` embarque deja toutes
            // celles de l'application, dont src/styles/print.css qui porte le contrat
            // d'impression (format de page, echelle, sauts de page, grilles). Injecter
            // des regles supplementaires a cet endroit, c'est recreer la dispersion que
            // print.css a justement supprimee.
            printWindow.document.open();
            printWindow.document.write(`
            <html lang="fr">
                <head>
                    <meta charset="UTF-8" />
                    <title>${fileName}</title>
                    ${styles}
                </head>
                <body>
                    ${header}
                    ${suiviHeaderHTML}
                    ${content.outerHTML}
                </body>
            </html>
        `);
            printWindow.document.close();

            // La boite d'impression ne doit s'ouvrir qu'une fois les feuilles de style
            // ET LES IMAGES de la fenetre d'impression chargees.
            //
            // Attendre les images du document SOURCE (etape 2 ci-dessus) ne suffit pas :
            // la fenetre d'impression est un document neuf, ses <img> relancent leurs
            // propres requetes. Quand `print()` partait avant leur arrivee, une carte
            // prestataire sortait sans son logo — de facon aleatoire, selon que l'image
            // etait deja en cache ou non. C'est le defaut constate sur le descriptif
            // procedural du MS.
            //
            // `onload` seul ne suffit pas non plus : si le document est deja `complete`
            // quand on pose le gestionnaire, l'evenement est passe et l'impression ne
            // part jamais. On sonde donc l'etat plutot que d'attendre un evenement.
            let dejaImprime = false;
            const lancerImpression = () => {
                if (dejaImprime) return;
                dejaImprime = true;
                printWindow.focus();
                printWindow.print();
            };

            const imagesPretes = () =>
                Array.from(printWindow.document.images).every((img) => img.complete);

            const debutAttente = Date.now();
            const attendreRessources = () => {
                if (dejaImprime || printWindow.closed) return;
                const documentPret = printWindow.document.readyState === "complete";
                // Plafond : une image ou une feuille distante qui ne repond pas ne doit pas
                // laisser la fenetre ouverte sans boite d'impression. On imprime alors avec
                // ce qu'on a plutot que de ne rien imprimer du tout.
                const tropLong = Date.now() - debutAttente > 8000;
                if ((documentPret && imagesPretes()) || tropLong) {
                    lancerImpression();
                } else {
                    setTimeout(attendreRessources, 100);
                }
            };
            attendreRessources();

        } catch (error) {
            console.error("Erreur lors de la préparation du PDF :", error);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Button
            value={isLoading ? "Préparation..." : value}
            icon={<PdfIcon />}
            variant="secondary"
            className={`${className} no-print`}
            onClick={handlePrint}
            size={size}
            disabled={isLoading || disabled}
        />
    );
};